import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Modal, Spin, message } from 'antd';
import { EyeOutlined, UndoOutlined, LoadingOutlined } from '@ant-design/icons';
import App from '../LayoutMobile';
import moment from "moment";
import CancelIcon from '../../Assets/CancelIcon.png';
import { Config } from '../Common/Config';
import './SignalDetailsData.css';

function SignalDetailsData() {
  const location = useLocation();
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const category = location.state?.category;
  const clinic_id = location.state?.clinic_id;
  const allClinicData = location.state?.allClinicData;
  // console.log("category:::",location.state)

  const formatTimeForDisplay = (timeValue) => {
    if (!timeValue) return '';

    // Keep the wall-clock time from API payloads (e.g. "2026-03-27 10:15:00.000")
    // to avoid timezone-based shifts in UI.
    if (typeof timeValue === 'string') {
      const directTimeMatch = timeValue.match(/\b(\d{2}:\d{2})(?::\d{2}(?:\.\d{1,3})?)?\b/);
      if (directTimeMatch) {
        return directTimeMatch[1];
      }
    }

    const parsed = moment(
      timeValue,
      [
        'HH:mm:ss',
        'HH:mm',
        'YYYY-MM-DD HH:mm:ss.SSS',
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601,
      ],
      true
    );

    return parsed.isValid() ? parsed.format('HH:mm') : '';
  };

  const toTitleCase = (value) => {
    if (!value || typeof value !== 'string') return value || 'N/A';

    return value
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const buildPreferenceDataSource = (items = []) => {
    return items.map((item, idx) => {
      const patientNames = (item.appointments || [])
        .map((appointment) => `${appointment?.first_name || ''} ${appointment?.last_name || ''}`.trim())
        .filter(Boolean);

      const uniquePatientNames = Array.from(new Set(patientNames));

      // status "1" = active, status "0" = inactive/archived
      const isArchived = item.status === 0 || item.status === "0";

      return {
        key: idx,
        id: item.id,
        date_of_conflict: item.date_of_conflict,
        date: item.date_of_conflict,
        clinic_name: item.clinic_name,
        room_name: item.room_name,
        appointments: item.appointments,
        patient_names: uniquePatientNames.join(', '),
        clinician: item.clinician,
        clinic_id: item.clinic_id,
        status: item.status,
        isArchived: isArchived,
      };
    });
  };

  const [preferenceDataSource, setPreferenceDataSource] = useState([]);
  const [loadingArchiveRecordId, setLoadingArchiveRecordId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (category?.id === 'preference_conflict_alert') {
      // TODO: Implement API fetch for preference alerts
      // const fetchPreferenceAlerts = async () => {
      //   try {
      //     const token = localStorage.getItem('token');
      //     if (!token) {
      //       console.error('Authorization token not found');
      //       return;
      //     }
      //
      //     const response = await fetch(
      //       `${Config.url}api/admin/v1/conflict-alerts`,
      //       {
      //         method: 'GET',
      //         headers: {
      //           'Content-Type': 'application/json',
      //           'Accept': 'application/json',
      //           'Authorization': `Bearer ${token}`,
      //         },
      //       }
      //     );
      //
      //     if (!response.ok) {
      //       throw new Error('Failed to fetch alerts');
      //     }
      //
      //     const responseData = await response.json();
      //     const preferenceAlerts = responseData.preference_conflict_alert?.data || [];
      //     setPreferenceDataSource(buildPreferenceDataSource(preferenceAlerts));
      //   } catch (error) {
      //     console.error('Error fetching preference alerts:', error);
      //   }
      // };
      //
      // fetchPreferenceAlerts();
      
      // Using existing category.data
      setPreferenceDataSource(buildPreferenceDataSource(category.data || []));
    }
  }, [category?.id, category?.data]);

  const handleArchiveRecord = async (record) => {
    try {
      if (!record.id) {
        console.error('Alert ID not available for record:', record);
        messageApi.error('Alert ID not found');
        return;
      }

      setLoadingArchiveRecordId(record.key);

      const newArchiveStatus = !record.isArchived;
      const status = newArchiveStatus ? 0 : 1;
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('Authorization token not found in localStorage');
        messageApi.error('Authorization token not found');
        setLoadingArchiveRecordId(null);
        return;
      }

      const response = await fetch(
        `${Config.url}conflict-alerts/${record.id}/toggle-archive?status=${status}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to toggle archive status');
      }

      setPreferenceDataSource((prev) =>
        prev.map((item) =>
          item.key === record.key ? { ...item, isArchived: newArchiveStatus } : item,
        ),
      );

      const actionText = newArchiveStatus ? 'disabled' : 'reactivated';
      messageApi.success(`Alert ${actionText} successfully!`);
    } catch (error) {
      console.error('Error toggling archive status:', error);
      messageApi.error('Failed to update alert status');
    } finally {
      setLoadingArchiveRecordId(null);
    }
  };

  if (!category) {
    return <div>No data available.</div>;
  }

  // Spacers Overwritten Table
  const handleCellClick = (record) => {
    // console.log('record:::', record);
    let clinic = allClinicData.find((x) => x.id == (record?.clinic_id || clinic_id));
    navigate('/clinic-calendar-page', {
      state: {
        ...location.state?.state,
        clinic: clinic,
        date: moment(record.date).format('YYYY-MM-DD') || '2025-05-15',
        redirectFrom: 'signal_details',
      },
    });
  };
  const spacersColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '25%',
      render: (text, record) => {
        return (
          <a
            href="#"
            style={{ color: '#008385', cursor: 'pointer' }}
            onClick={e => {
              e.preventDefault();
              handleCellClick(record);
            }}
          >
            {moment(record.date).format('DD/MM/YYYY')}
          </a>
        )
      },
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      width: '25%',
      render: (text, record) =>
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            handleCellClick(record);
          }}
          style={{ pointerEvents: 'none', color: 'gray' }}>{text}</a>,
    },
    {
      title: 'Clinic',
      dataIndex: 'clinic',
      key: 'clinic',
      width: '25%',
      render: (text, record) =>
        <a href="#"
          onClick={e => {
            e.preventDefault();
            handleCellClick(record);
          }}
          style={{ pointerEvents: 'none', color: 'gray' }}>{text}</a>,
    },
    {
      title: 'Room',
      dataIndex: 'room',
      key: 'room',
      width: '25%',
      render: (text, record) =>
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            handleCellClick(record);
          }}
          style={{ pointerEvents: 'none', color: 'gray' }}>{text}</a>,
    },
  ];

  // Existing Table
  const columns = [
    {
      title: 'Date of conflict',
      dataIndex: 'date_of_conflict',
      key: 'date_of_conflict',
      render: (text, record) => (
        <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{new Date(record.date_of_conflict).toLocaleDateString('en-GB')}</a>
      ),
    },
    {
      title: 'Clinic',
      dataIndex: 'clinic_name',
      key: 'clinic_name',
      render: (text) => <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{text}</a>,
    },
    {
      title: 'Room',
      dataIndex: 'room_name',
      key: 'room_name',
      render: (text) => <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{text}</a>,
    },
    {
      title: 'No. of appointments',
      dataIndex: 'appointments',
      key: 'appointments',
      render: (appointments) => (
        <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{appointments.length.toString().padStart(2, '0')}</a>
      ),
    },
    {
      title: 'Clinician',
      dataIndex: 'clinician',
      key: 'clinician',
      render: (clinician) => (
        <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{clinician.name} {clinician.surname}</a>
      ),
    },
    {
      title: 'Cancellation date',
      dataIndex: 'clinician',
      key: 'cancellation_date',
      render: (clinician) => (
        <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{new Date(clinician.cancelled_at).toLocaleDateString('en-GB')}</a>
      ),
    },
    {
      title: 'Cancellation time',
      dataIndex: 'clinician',
      key: 'cancellation_time',
      render: (clinician) => (
        <a href="#" style={{ pointerEvents: 'none', color: 'gray' }}>{new Date(clinician.cancelled_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</a>
      ),
    },
  ];

  const preferenceColumns = [
    {
      title: 'Date of conflict',
      dataIndex: 'date_of_conflict',
      key: 'date_of_conflict',
      render: (text, record) => (
        <a
          href="#"
          style={{ color: '#008385', cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            handleCellClick(record);
          }}
        >
          {new Date(text).toLocaleDateString('en-GB')}
        </a>
      ),
    },
    {
      title: 'Clinic',
      dataIndex: 'clinic_name',
      key: 'clinic_name',
    },
    {
      title: 'Room',
      dataIndex: 'room_name',
      key: 'room_name',
    },
    {
      title: 'No. of appointments',
      dataIndex: 'appointments',
      key: 'appointments',
      render: (appointments) => <span>{appointments ? appointments.length : 0}</span>,
    },
    // {
    //   title: 'Patient(s)',
    //   dataIndex: 'patient_names',
    //   key: 'patient_names',
    //   render: (patientNames) => <span>{patientNames || 'N/A'}</span>,
    // },
    {
      title: 'Clinician',
      dataIndex: 'clinician',
      key: 'clinician',
      render: (clinician) => clinician ? <span>{clinician.name} {clinician.surname}</span> : '',
    },
    // {
    //   title: 'Status',
    //   dataIndex: 'isArchived',
    //   key: 'status',
    //   render: (isArchived) => <span>{isArchived ? 'Inactive' : 'Active'}</span>,
    // },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <EyeOutlined
            style={{ cursor: 'pointer', color: '#008385', fontSize: '18px' }}
            onClick={() => {
              setSelectedRecord(record);
              setModalVisible(true);
            }}
          />
          {loadingArchiveRecordId === record.key ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: '#008385' }} spin />} />
          ) : record.isArchived ? (
            <UndoOutlined
              title="Reactivate"
              style={{ cursor: 'pointer', color: '#8c8c8c', fontSize: '18px' }}
              onClick={() => handleArchiveRecord(record)}
            />
          ) : (
            <img
              src={CancelIcon}
              alt="Disable"
              title="Disable"
              style={{ cursor: 'pointer', width: 18, height: 18 }}
              onClick={() => handleArchiveRecord(record)}
            />
          )}
        </div>
      ),
    },
  ];

  const dataSource = (category.data || []).map((item, idx) => {
    const patientNames = (item.appointments || [])
      .map((appointment) => `${appointment?.first_name || ''} ${appointment?.last_name || ''}`.trim())
      .filter(Boolean);

    const uniquePatientNames = Array.from(new Set(patientNames));

    return {
      key: idx,
      date_of_conflict: item.date_of_conflict,
      date: item.date_of_conflict,
      clinic_name: item.clinic_name,
      room_name: item.room_name,
      appointments: item.appointments,
      patient_names: uniquePatientNames.join(', '),
      clinician: item.clinician,
      clinic_id: item.clinic_id,
    };
  });

  return (
    <App>
      {contextHolder}
      <h3 style={{ textAlign: 'left', fontSize: '20px' }}>{category.label_name}</h3>
      {/* {(category && clinic_id) && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <button style={{
            border: '2px solid #008385',
            borderRadius: 8,
            background: 'none',
            color: '#555',
            padding: '10px 32px',
            fontSize: 18,
            marginRight: 16,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontWeight: 400,
            boxSizing: 'border-box',
          }}>
            Filter "No gender"
          </button>
        </div>
      )} */}
      {category.id === 'spacers_overwritten_alert' ? (
        <Table
          dataSource={(category.data || []).map((item, idx) => ({
            ...item,
            key: idx,
            date: item.date,
            time: formatTimeForDisplay(item.start_time),
            clinic: item.clinic_name,
            room: item.room_name,
          }))}
          columns={spacersColumns}
          pagination={{ pageSize: 20 }}
          rowKey="key"
          onRow={(record) => ({
            onClick: () => handleCellClick(record),
            style: { cursor: 'pointer' }
          })}
          bordered
        />
      ) : category.id === 'preference_conflict_alert' ? (
        <>
          <Table
            dataSource={preferenceDataSource}
            columns={preferenceColumns}
            pagination={{ pageSize: 20 }}
            rowKey="key"
            rowClassName={(record) => (record.isArchived ? 'disabled-alert-row' : '')}
            bordered
          />
          <Modal
            title={`${selectedRecord?.clinic_name || category?.clinic_name || ''} ${selectedRecord?.room_name || ''}`.trim()}
            visible={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            width={800}
          >
            {(() => {
              const appointmentsToShow =
                (selectedRecord && selectedRecord.appointments && selectedRecord.appointments.length > 0
                  ? selectedRecord.appointments
                  : category.data?.flatMap(item => item.appointments || [])) || [];

              if (appointmentsToShow.length === 0) {
                return <p>No appointments available.</p>;
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {appointmentsToShow.map((app, idx) => {
                    // console.log("app:::", app);
                    const details = app.conflict_details || {};
                    const gender = details.gender_conflict;
                    const language = details.language_conflict;

                    return (
                      <div key={idx} style={{ border: '1px solid #e8e8e8', borderRadius: 12, padding: 16, backgroundColor: '#fff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#08979c', fontWeight: 700 }}>
                              {idx + 1}
                            </div>
                            <div>
                              <div style={{ fontSize: 14, fontWeight: 700 }}>{app.first_name || ''} {app.last_name || ''}</div>
                              <div style={{ color: '#2b2b2b', fontSize: 12,fontWeight: 700 }}>{app.start_time ? `${formatTimeForDisplay(app.start_time)} - ${app.end_time ? formatTimeForDisplay(app.end_time) : ''}` : 'Time N/A'}</div>
                            </div>
                          </div>
                          {/* <div style={{ color: '#008385', fontWeight: 600 }}>{app.status || 'N/A'}</div> */}
                        </div>
                        {/* <div style={{ color: '#333', marginBottom: 8 }}>
                          Room: {app.room_name || app.room || selectedRecord?.room_name || 'N/A'}
                        </div> */}
                        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                          {gender?.appointment_gender && (
                          <div style={{ flex: 1, border: '1px solid #e6e6e6', borderRadius: 8, padding: 12 }}>
                            <div style={{ color: '#2b2b2b',marginBottom: 8}}>
                              <span style={{ fontWeight: 700 }}>Patient Name:</span>{' '}
                              {(app.patient?.first_name || app.first_name || '').trim()} {(app.patient?.last_name || app.last_name || '').trim()}
                            </div>
                            <div style={{  color: '#2b2b2b',fontWeight: 700,marginTop:10,fontSize:16,marginBottom:5 }}>GENDER PREFERENCE</div>
                            <div style={{ color: '#2b2b2b' }}><span style={{ fontWeight: 700 }}>Appointment Preference:</span> {toTitleCase(gender?.appointment_gender)}</div>
                            {/* <div style={{ fontSize: 14, fontWeight: 600 }}>{gender ? gender.appointment_gender : 'N/A'}</div> */}
                            {gender && gender.clinician_gender && (
                              <div style={{ marginTop: 4, color: '#2b2b2b' }}><span style={{ fontWeight: 700 }}>Clinician Assigned:</span> {toTitleCase(gender?.clinician_gender)}</div>
                            )}
                          </div>
                          )}
                          {language?.appointment_language && (
                          <div style={{ flex: 1, border: '1px solid #e6e6e6', borderRadius: 8, padding: 12 }}>
                            <div style={{ color: '#2b2b2b',}}>
                              <span style={{ fontWeight: 700 }}>Patient Name:</span>{' '}
                              {(app.patient?.first_name || app.first_name || '').trim()} {(app.patient?.last_name || app.last_name || '').trim()}
                            </div>
                            <div style={{  color: '#2b2b2b',fontWeight: 700 }}>LANGUAGE PREFERENCE</div>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{language ? language.appointment_language : 'N/A'}</div>
                            {language && language.language_conflict_alert && (
                              <div style={{ marginTop: 4, color: '#2b2b2b' }}>{language.language_conflict_alert}</div>
                            )}
                          </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </Modal>
        </>
      ) : (
        <>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={{ pageSize: 20 }}
            rowKey="key"
            onRow={(record) => ({
              onClick: () => handleCellClick(record),
              style: { cursor: 'pointer' }
            })}
            bordered
          />
          <div style={{ color: '#008385' }}>
            Once a conflict has been resolved here, it might take up to 15 minutes for the alert to disappear from this page due to the schedule of Lantum synchronisation.
          </div>
        </>
      )}
    </App>
  );
}

export default SignalDetailsData;